import "#legacy/handlebars_helpers/icon_helper.js";
import "#legacy/admin/page_builder/namespace.js";
import "#legacy/admin/page_builder/templates/wysiwyg-field-template.hbs";
import DOMPurify from "dompurify";

import { attachHtmlMode, SANITIZE_POLICY } from "#/admin/wysiwyg/html-mode";
import { isDisabledByShowOn } from "#/admin/legacy/forms/input-ownership.js";
import { getQuillHTML, setQuillHTML } from "#/shared/lib/quill-html";

(function (_) {
  "use strict";

  // Quill's allowed-formats whitelist. Any format not listed here is stripped
  // from the editor and its toolbar button is a no-op. 'header' is intentionally
  // absent — it's added per-field by `attach` only when a heading toolset is
  // requested, so heading buttons stay opt-in.
  var DEFAULT_FORMATS = [
    "bold",
    "italic",
    "underline",
    "strike",
    "indent",
    "list",
    "code",
    "link",
    "telephone",
    "snippet",
    "mark",
  ];

  /**
   * @name initialize
   * @description
   *  Factory function for creating a new instance of the wysiwyg
   *   field, as well as setting basic defaults for control over the
   *   instance.
   *
   * @param {HTMLElement|jQuery} element
   * @param {object} config
   */
  var initialize = function (element, config) {
    // if this is a jquery wrapped element than we need to
    //  go ahead and find the root element instead
    // var $toolbar = element.parent().find('.toolbar');
    var $element = $element;
    if (!($element instanceof jQuery)) {
      $element = jQuery(element);
    }

    // inherit or set the default configuration
    config = _.extend(
      {
        theme: "sparkle",
        formats: DEFAULT_FORMATS,
      },
      config,
    );

    // Set up default modules
    config.modules = _.extend(
      {
        toolbar: {
          container: $element.parent().find(".toolbar").get(0),
        },
      },
      config.modules,
    );

    // Autodetect modules
    // If the sparkle AI module is enabled, set up its container
    if (config.modules.sparkle_ai) {
      config.modules.sparkle_ai = _.extend(
        {
          container: $element.parent().find("[ref=ql-sparkle-ai]").get(0),
        },
        config.modules.sparkle_ai,
      );
    }

    return new Quill($element.get(0), config);
  };

  /**
   * @name render
   * @description
   *   Return an HTML string containing the wysiwyg dom elements
   *    The requested toolset is specified as a space separated list.
   * @param toolset [string]
   * @returns [string] an HTML string
   */
  var render = function (toolsetString) {
    var options = toolsetOptions(toolsetString);

    if (options.html) {
      options.htmlModeLabels = htmlModeLabels();
    }

    return getTemplate()(options);
  };

  /**
   * @name attach
   * @description
   *  Find all the instances of quill currently present on the page and
   *   attach the quill behaviour to them, including the correct template.
   */
  var attach = function (elements, opts) {
    var deferred = new jQuery.Deferred();
    var elementsUninitialized = jQuery(elements).length;

    if (elementsUninitialized == 0) {
      deferred.resolve();
      return;
    }

    var trackInitialize = function () {
      elementsUninitialized--;

      if (!elementsUninitialized) {
        deferred.resolve();
      }
    };

    // Quill elements are initialised once the DOM is ready.
    jQuery(function () {
      jQuery(elements).each(function () {
        var $this = jQuery(this);

        if ($this[0].wysiwyg) {
          trackInitialize();
          return;
        } // reinitializing a Quill instance creates a duplicate WYSIWYG

        var quillConfig = jQuery.extend(
          {
            modules: {},
          },
          $this.data("quill-config"),
        );

        var toolset = $this.data("wysiwyg-toolset") || "basic";

        // Heading buttons only do anything if "header" is in Quill's allowed-formats
        // whitelist, otherwise clicking them is a silent no-op. Resolve the toolset
        // first so group aliases like "headings" (which expand to h2/h3/h4) are
        // detected, not just the literal h2/h3/h4 tokens. Append to the existing
        // whitelist rather than replacing it so the other formats keep working.
        var resolvedTools = toolsetOptions(toolset);
        if (resolvedTools.h2 || resolvedTools.h3 || resolvedTools.h4) {
          quillConfig.formats = (quillConfig.formats || DEFAULT_FORMATS).concat("header");
        }

        // If the field has a SparkleAI menu, add the sparkleAI toolbar item
        var sparkleAIMenuUrl = $this.data("sparkle-ai-menu-url");
        if (sparkleAIMenuUrl) {
          toolset = "sparkleAI " + toolset;
          quillConfig.modules.sparkle_ai = { menuUrl: sparkleAIMenuUrl };
        }

        var options = jQuery.extend(
          {
            toolset: toolset,
          },
          opts,
        );

        var $template = jQuery(render(options.toolset));
        $this.after($template);

        var editor = initialize($template.find(".editor"), quillConfig);
        $this.prop("wysiwyg", editor); // Store the editor on the element so we can access it if desired. Use a property so we can access it without jQuery

        // A non-native field is read through `.text()`. Its `.value` is written alongside, because once
        // anything sets a textarea's `.value` (the review pane does), the value stops following its text.
        var readField = function () {
          return options.native === true ? $this[0].value : $this[0].textContent;
        };

        // Stores `html` in the field. It fires no events when the HTML is unchanged, or with
        // `announce: false`, the way setting a native control's value from code does, so form listeners
        // only hear the user's edits. The preview is refreshed either way.
        var writeField = function (html, { announce = true } = {}) {
          if (html === "<p><br></p>") {
            html = "";
          }

          announce = announce && html !== readField();

          if (options.native === true) {
            $this[0].value = html;
            if (announce) {
              $this[0].dispatchEvent(new CustomEvent("input"));
            } else {
              updatePreview();
            }
          } else {
            $this[0].textContent = html;
            $this[0].value = html;
            if (announce) {
              $this[0].dispatchEvent(new Event("input", { bubbles: true }));
              $this[0].dispatchEvent(new Event("change", { bubbles: true }));
            } else {
              updatePreview();
            }
          }
        };

        var htmlMode =
          toolsetOptions(options.toolset).html &&
          attachHtmlMode({
            field: $this[0],
            editor: editor,
            container: $template[0],
            read: readField,
            write: writeField,
          });

        if ($this.is(":disabled") && !isDisabledByShowOn($this[0])) {
          editor.enable(false);
        }

        if (htmlMode) {
          htmlMode.load($this[0].textContent);
        } else {
          setQuillHTML(editor, $this[0].textContent);
        }
        trackInitialize();

        // if the found dom element is a text area then we want to bind
        //  the change event of the editor to update the textarea as well
        //  so that they stay in sync.
        var $bindingContext = jQuery($this.data("binding-context"));

        // In HTML mode the field holds hand-written HTML that Quill's copy must not overwrite.
        function syncFieldFromEditor() {
          if ($this[0].wysiwygHtmlMode?.isActive()) {
            return;
          }

          writeField(getQuillHTML(editor));
        }

        // Shows the field's HTML in its binding element. The preview shares the admin origin, so
        // hand-written HTML is sanitized before it reaches it. Quill's own HTML holds no active content.
        // Does nothing before the binding context is resolved, which is the case while the editor loads.
        function updatePreview() {
          if (!$bindingContext || !$this.data("binding-element")) {
            return;
          }

          var html = $this.text() || editor.root.getAttribute("data-placeholder");
          if ($this[0].wysiwygHtmlMode?.isActive()) {
            html = DOMPurify.sanitize(html, SANITIZE_POLICY);
          }

          $bindingContext
            .contents()
            .find($this.data("binding-element"))
            .each(function () {
              this.innerHTML = html;
            });
        }

        if ($this.is("textarea") || $this.is("input")) {
          $this.hide();
          editor.on("text-change", syncFieldFromEditor);
          $this.on("input change", updatePreview);
        } else {
          // if the found dom element is anything but a textarea or input,
          //  then we can just go ahead and remove it.
          $this.remove();
        }
      });
    });

    return deferred.promise();
  };

  /**
   * @private
   * @name getTemplate
   * @description
   *  Factory method which will return the handlebar template
   *   representing a wysiwyg implementation dom element.
   *
   * @return {Handlebars.Template}
   */
  var getTemplate = function () {
    return Sparkle.HandlebarsTemplates["wysiwyg-field-template"];
  };

  /**
   * @private
   * Return a mapping of tool options to be passed to the wysiwyg-field-template.
   * The toolset is specified as a space separated list.
   *
   * @example
   *   toolsetOptions('formatting layout') // => { bold: true, italic: true, underline: true, bulletedList: true, orderedList: true, noBreakFormat: true }
   *
   * @param toolset [string]
   * @returns [object] a mapping of tool options
   */
  var toolsetOptions = function (toolset) {
    if (toolset == null) {
      return {};
    }

    var tools = toolset
      .split(" ")
      .flatMap(resolveTool)
      .filter((tool) => tool != null);

    var opts = {};
    tools.forEach((tool) => {
      opts[tool] = true;
    });

    return opts;
  };

  var ToolsetDescriptions = {
    basic: ["formatting", "layout"],
    extended: ["extendedFormatting", "layout", "advanced"],
    extendedFormatting: ["formatting", "strikethrough"],
    formatting: ["bold", "italic", "underline"],
    layout: ["bulletedList", "orderedList"],
    advanced: ["link", "phoneNumber", "snippet"],
    bold: "bold",
    italic: "italic",
    underline: "underline",
    strikethrough: "strickthrough",
    bulletedList: "bulletedList",
    orderedList: "orderedList",
    link: "link",
    phoneNumber: "phoneNumber",
    snippet: "snippet",
    modalView: "modalView",
    noBreakFormat: "noBreakFormat",
    sparkleAI: "sparkleAI",
    headings: ["h2", "h3", "h4"],
    h2: "h2",
    h3: "h3",
    h4: "h4",
    html: "html",
  };

  var htmlModeLabels = function () {
    return {
      toggle: Sparkle.t("admin.wysiwyg.html_mode.toggle"),
      sourceLabel: Sparkle.t("admin.wysiwyg.html_mode.source_label"),
      unsupportedNotice: Sparkle.t("admin.wysiwyg.html_mode.unsupported_notice"),
      lossyWarning: Sparkle.t("admin.wysiwyg.html_mode.lossy_warning"),
      switchAnyway: Sparkle.t("admin.wysiwyg.html_mode.switch_anyway"),
      keepEditing: Sparkle.t("admin.wysiwyg.html_mode.keep_editing"),
    };
  };

  var resolveTool = function (tool) {
    var name = ToolsetDescriptions[tool];
    if (name == null) {
      return;
    }

    if (Object.prototype.toString.call(name) === "[object Array]") {
      return name.flatMap(resolveTool); // allows ToolsetDescriptions to be self-referential within arrays
    }

    return [name];
  };

  /**
   * This is the main component helper for creating, instantiating and handling
   *  all wysiwyg fields. It will also provide an immediate way to attach
   *  quill behaviour through simple data properties rather than the normal
   *  api.
   */
  Sparkle.Components.Wysiwyg = {
    /**
     * @see initialize
     */
    initialize: initialize,

    /**
     * @see render
     */
    render: render,

    /**
     * @see attach
     */
    attach: attach,
  };
})(_);

Sparkle.Components.Wysiwyg.attach("[data-behaviour~=quill]");
